import { AppInfoLoader, IAppInfoType } from '@services/AppInfoLoader';
import { DeviceDetails } from '@utils/DeviceDetails';
import { CrossFrameUtils } from '@utils/CrossFrameUtils';

import _ from 'lodash';
import { EnvironmentDetail } from '@src/utils/EnvironmentDetailUtils';

interface IDataType {
    type: string;
    name: string;
    value: any;
    storeId: string;
    program: string;
    operation: any;
    startTime: number;
    endTime: number;
    clientProgram?: any;
    dimensions?: any;
}
interface IDeviceDetailsType {
    [x: string]: any;
    deviceSerialNumber?: string;
}

/**
 *  This is meant to be an abstract base class for the Metric Objects.
 *
 *  @param storeId - The StoreId where this metric was captured
 *  @param name - The name of the metric we are recording
 *  @param program - Name of program application (maps to "Program" key in PMET).
 *  @param operation - Name of operation (maps to "Operation" key in PMET).
 */
export class Metrics {
    /**
     * Creates a default metrics generator that defines the program and leaves the
     * resolution of storeId up to the Metric implementation.
     */
    public static defaultMetricGenerator(): Readonly<Metrics> {
        const defaultMetric = new Metrics('', undefined, '', 'IhmFlyingDutchman', '');
        // StoreID Explicitly set to undefined to let the metric resolve the current store
        // for us: https://tiny.amazon.com/6r65g6bt/codeamazpackIhmCblobf5fdsour
        return Object.freeze(defaultMetric);
    }
    protected startTime;
    protected stopTime;
    private type;
    private storeId;
    private name;
    private program;
    private operation;
    private value;
    private clientProgram;
    private instance;
    private dimensions;
    private toolNameOverride;

    constructor(type: string, storeId: string, name: string, program: string, operation: string) {
        this.type = type;
        this.storeId = storeId;
        this.name = name;
        this.program = program;
        this.operation = operation;

        this.startTime = null;
        this.stopTime = null;
        this.value = null;

        this.clientProgram = null;
        this.instance = null;
    }

    public setStartTime(time: number) {
        this.startTime = time;
    }

    public setStopTime(time: number) {
        this.stopTime = time;
    }

    public setValue(value) {
        this.value = value;
    }

    /**
     * Use with care - if you filter by clientProgram in PMET, you cannot filter by MethodName or Metric
     */
    public setClientProgram(clientProgram) {
        this.clientProgram = clientProgram;
    }

    /**
     * Used to set MetricClass and Instance in PMET
     * Structure: {key : METRIC_CLASS, value : INSTANCE}
     */
    public setDimensions(dimensions) {
        this.dimensions = dimensions;
    }

    /**
     * Overrides the ToolName dimension that would otherwise be resolved from
     * AppInfoLoader in the frame that broadcasts the metric. Use this when the
     * caller (e.g. a sub-tool making a cross-frame request) needs the metric to
     * be attributed to its own tool rather than the frame doing the work.
     */
    public setToolName(name: string) {
        this.toolNameOverride = name;
    }

    public getData(): IDataType {
        const data: IDataType = {
            type: this.type,
            name: this.name,
            value: this.value,
            storeId: this.storeId,
            program: this.program,
            operation: this.operation,
            startTime: this.startTime,
            endTime: this.stopTime,
        };
        if (this.clientProgram) {
            data.clientProgram = this.clientProgram;
        }
        if (this.dimensions) {
            data.dimensions = this.dimensions;
        }
        return data;
    }

    /**
     *  Broadcasts the metric on the root window frame so that it can be picked up
     *  by the MetricsRecorder
     */
    public async broadcastMetric() {
        let storeIdPromise: Promise<void | IAppInfoType> = Promise.resolve();
        if (this.storeId === undefined) {
            try {
                const config = await AppInfoLoader.getAppConfig();
                this.storeId = config.store;
                storeIdPromise = Promise.resolve(config);
            } catch (error) {
                console.error(error);
            }
        }
        try {
            const configData = await AppInfoLoader.getAppConfig();
            this.dimensions = this.dimensions || [];
            const effectiveToolName = this.toolNameOverride || configData.toolName || 'Unknown';
            this.dimensions.push({ key: 'ToolName', value: effectiveToolName });
            if (_.isString(configData.badgeId) && configData.badgeId.length > 0) {
                this.dimensions.push({ key: 'AuthId', value: configData.badgeId });
            }
            if (EnvironmentDetail.isAndroid()) {
                const deviceDetails: IDeviceDetailsType = await DeviceDetails.getDeviceDetails();
                if (_.isObject(deviceDetails)) {
                    const deviceSerialNumber = deviceDetails.deviceSerialNumber;
                    if (_.isString(deviceSerialNumber) && deviceSerialNumber.length > 0) {
                        this.dimensions.push({ key: 'DSN', value: deviceSerialNumber });
                    }
                }
            }
            const response = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window,
                message: {
                    method: 'RecordMetric',
                    data: _.cloneDeep(this.getData()),
                },
            });
        } catch (error) {
            console.error(error);
        }
    }
}
